import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  WritableSignal,
  inject,
  signal,
} from '@angular/core';
import { UntypedFormBuilder, Validators, type UntypedFormGroup } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { catchError, filter, map, Observable, of, Subscription, take } from 'rxjs';

import { ElementActions, ElementTypes, GaContext, UserAgentService } from '@fcom/common';
import { GtmService } from '@fcom/common/gtm';
import { ConfigService, mapErrorForSentry, PNR_PATTERN, SentryLogger, WindowRef, type AppState } from '@fcom/core';
import { type Profile } from '@fcom/core-api/login';
import { profileOrUndefinedIfNotLoggedIn } from '@fcom/core/selectors';
import { EMAIL_PATTERN, NAME_PATTERN } from '@fcom/core/utils';
import { ButtonMode, ButtonSize, ButtonType, InputType, TextInputType } from '@fcom/ui-components';
import { LanguageService } from '@fcom/ui-translate';

import { PNR, PassengerNameRecord } from '../../../common-booking';
import {
  AgentforceChatConfig,
  HelpButtonStatus,
  PrechatFields,
  AgentforceHiddenProfileFields,
  AgentforceChatFields,
  ChatVisitorIpResponse,
} from '../../interfaces';
import { AgentforceChatService } from '../../services/agentforce-chat.service';

export enum SalesforceChatFormControl {
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  EMAIL = 'email',
  HAS_BOOKING_REFERENCE_Q = 'bookingRefQ',
  BOOKING_REFERENCE = 'bookingReference',
}

export interface SalesforceChatForm {
  [SalesforceChatFormControl.FIRST_NAME]: string;
  [SalesforceChatFormControl.LAST_NAME]: string;
  [SalesforceChatFormControl.EMAIL]: string;
  [SalesforceChatFormControl.HAS_BOOKING_REFERENCE_Q]: string;
  [SalesforceChatFormControl.BOOKING_REFERENCE]: string;
}

export enum SalesforceChatFormHasBookingRefQ {
  YES = 'yesBookingReference',
  NO = 'noBookingReference',
}

@Component({
  selector: 'fin-chat-user-form',
  templateUrl: './chat-user-form.component.html',
  styleUrl: './chat-user-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ChatUserFormComponent implements OnInit, OnDestroy {
  private fb = inject(UntypedFormBuilder);
  private store$ = inject<Store<AppState>>(Store);
  private languageService = inject(LanguageService);
  private configService = inject(ConfigService);
  private afChatService = inject(AgentforceChatService);
  private gtmService = inject(GtmService);
  private windowRef = inject(WindowRef);
  private userAgentService = inject(UserAgentService);
  private http = inject(HttpClient);
  private sentryLogger = inject(SentryLogger);
  private destroyRef = inject(DestroyRef);

  readonly ButtonType = ButtonType;
  readonly ButtonMode = ButtonMode;
  readonly ButtonSize = ButtonSize;
  readonly SalesforceChatFormControl = SalesforceChatFormControl;
  readonly textareaLimit30 = 30;
  readonly TextInputType = TextInputType;
  readonly InputType = InputType;
  readonly SalesforceChatFormHasBookingRefQ = SalesforceChatFormHasBookingRefQ;

  private subscriptions: Subscription = new Subscription();

  agentforceConfig?: AgentforceChatConfig;
  hiddenFields: AgentforceHiddenProfileFields;
  reactiveForm: UntypedFormGroup;
  loading: WritableSignal<boolean> = signal(false);

  constructor() {
    this.agentforceConfig = this.configService.cfg.agentforceChat;
  }

  ngOnInit(): void {
    if (!this.afChatService.renderAudioElement$.value) {
      this.afChatService.renderAudioElement$.next(true);
    }

    this.subscriptions.add(
      this.store$.pipe(profileOrUndefinedIfNotLoggedIn(), take(1)).subscribe((profile: Profile): void => {
        this.createForm(profile);
        this.addFormWatcher();
        this.hiddenFields = this.setHiddenFields(profile);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get selectedBookingRef(): SalesforceChatFormHasBookingRefQ {
    return this.reactiveForm.controls[SalesforceChatFormControl.HAS_BOOKING_REFERENCE_Q].value ?? null;
  }

  createForm(profile?: Profile): void {
    this.reactiveForm = this.fb.group({
      [SalesforceChatFormControl.FIRST_NAME]: [
        profile?.firstname || '',
        [Validators.required, Validators.pattern(NAME_PATTERN), Validators.maxLength(this.textareaLimit30)],
      ],
      [SalesforceChatFormControl.LAST_NAME]: [
        profile?.lastname || '',
        [Validators.required, Validators.pattern(NAME_PATTERN), Validators.maxLength(this.textareaLimit30)],
      ],
      [SalesforceChatFormControl.EMAIL]: [
        profile?.email || '',
        [Validators.required, Validators.pattern(EMAIL_PATTERN)],
      ],
      [SalesforceChatFormControl.HAS_BOOKING_REFERENCE_Q]: this.fb.control(
        '' as SalesforceChatFormHasBookingRefQ,
        Validators.required
      ),
      [SalesforceChatFormControl.BOOKING_REFERENCE]: ['', [Validators.pattern(PNR_PATTERN), Validators.maxLength(6)]],
    });
  }

  addFormWatcher(): void {
    this.subscriptions.add(
      this.reactiveForm.controls[SalesforceChatFormControl.HAS_BOOKING_REFERENCE_Q].valueChanges.subscribe(
        (value): void => {
          value === SalesforceChatFormHasBookingRefQ.YES
            ? this.enableBookingReference()
            : this.disableBookingReference();
        }
      )
    );
  }

  private enableBookingReference(): void {
    this.reactiveForm.controls[SalesforceChatFormControl.BOOKING_REFERENCE].setValidators([
      Validators.pattern(PNR_PATTERN),
      Validators.maxLength(6),
      Validators.required,
    ]);
    this.reactiveForm.get([SalesforceChatFormControl.BOOKING_REFERENCE]).reset();
    this.reactiveForm.controls[SalesforceChatFormControl.BOOKING_REFERENCE].enable();
    this.prefillBookingReferenceFromStore();
  }

  private disableBookingReference(): void {
    this.reactiveForm.controls[SalesforceChatFormControl.BOOKING_REFERENCE].setValidators(null);
    this.reactiveForm.get([SalesforceChatFormControl.BOOKING_REFERENCE]).reset();
  }

  private prefillBookingReferenceFromStore(): void {
    this.subscriptions.add(
      this.store$
        .pipe(
          PNR(),
          filter((pnr: PassengerNameRecord | null | undefined): pnr is PassengerNameRecord & { recLoc: string } =>
            Boolean(pnr?.recLoc?.trim())
          ),
          take(1)
        )
        .subscribe((pnr) =>
          this.reactiveForm.controls[SalesforceChatFormControl.BOOKING_REFERENCE].setValue(pnr.recLoc.trim())
        )
    );
  }

  setHiddenFields(profile: Profile): AgentforceHiddenProfileFields {
    if (profile) {
      return {
        Finnair_Plus_Number_c: profile.memberNumber,
        Case_Member_Tier_c: profile.tier, // Both variations requested to be added
        Member_Tier: profile.tier, // Both variations requested to be added
        AuthenticatedUser: 'true', // Must be a string
      };
    }
    return {
      AuthenticatedUser: 'false', // Must be a string
    };
  }

  websiteLanguage(): string {
    const ZH_LANG = 'zh';

    return this.languageService.localeValue.startsWith(ZH_LANG)
      ? this.languageService.localeValue
      : this.languageService.langKeyValue;
  }

  webMessagingChatData = (formData: PrechatFields, ipAddress?: string): AgentforceChatFields => {
    const bookingReferenceField: { Booking_Reference?: string } =
      this.selectedBookingRef === SalesforceChatFormHasBookingRefQ.YES
        ? { Booking_Reference: formData.bookingReference }
        : {};

    return {
      FirstName_c: formData.firstName,
      LastName_c: formData.lastName,
      Email_c: formData.email,
      ...this.hiddenFields,
      Visitor_IP_Address_c: ipAddress ?? '',
      WebSite_Country: this.languageService.ddsLocaleValue.countrySite.toLowerCase(),
      WebSite_Language: this.websiteLanguage(),
      Site_Origin: this.windowRef.nativeWindow.location.href,
      User_Agent: this.userAgentService.userAgent ?? null,
      Browser: this.userAgentService.getUserBroswer() ?? null,
      Browser_Language: navigator.language ?? null,
      ...bookingReferenceField,
    };
  };

  onSubmit(): void {
    const formData: PrechatFields = this.reactiveForm.value;

    if (!this.reactiveForm.valid) {
      this.reactiveForm.markAllAsTouched();
      this.reactiveForm.updateValueAndValidity();

      return;
    }

    this.getIp()
      .pipe(
        map((ipAddress): AgentforceChatFields => this.webMessagingChatData(formData, ipAddress)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((chatData): void => {
        this.initiateChat(chatData);
      });
  }

  private getIp(): Observable<string> {
    return this.http.get<ChatVisitorIpResponse>(`${this.configService.cfg.apiBaseUrl}/myip`).pipe(
      map((res): string => res.remote_addr),
      catchError((err: unknown): Observable<string> => {
        this.sentryLogger.warn('Chat user form: failed to get ip info', {
          error: mapErrorForSentry(err),
        });
        return of('Chat user form: unknown-ip');
      }),
      take(1)
    );
  }

  private initiateChat(chatData: AgentforceChatFields): void {
    this.afChatService.chatButtonState$.next(HelpButtonStatus.CHAT);
    this.loading.set(true);
    this.selectedBookingRef === SalesforceChatFormHasBookingRefQ.YES
      ? this.trackElement('start-the-chat_booking')
      : this.trackElement('start-the-chat');
    this.afChatService.openWebMessagingChat$.next(chatData);
  }

  private trackElement(label: string): void {
    this.gtmService.trackElement(label, GaContext.CHAT, ElementTypes.BUTTON, undefined, ElementActions.CLICK);
  }
}
